import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ALL_PLANS, PLAN_LABEL, type ScheduledSeatChange } from "@vd/api-client";
import { IconButton, Menu, Spinner } from "@vd/ui";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { z } from "zod";
import {
  type BillingSummary,
  billingSummaryQuery,
  canceling,
  choosesPlan,
  dayOf,
  fmtCents,
  renewalCents,
  trialEnded,
  trialLine,
  updating,
} from "../../../api/billingPlan";
import { errorMessage } from "../../../api/client";
import { planKeys, planQuery, plans } from "../../../api/plans";
import { requireAdmin } from "../../../auth/useMe";
import { CancelPlanDialog, useResumePlan } from "../../../components/billing/CancelPlanDialog";
import { ChangePlanSheet, type SheetMode } from "../../../components/billing/ChangePlanSheet";
import { BillingCard, BillingRow, PmLogo, pmName } from "../../../components/billing/parts";
import { toast } from "../../../components/toast";
import { Badge, Button, ErrorState, Loading } from "../../../components/ui";
import { Link, useNavigate } from "../../../lib/navigate";
import { billingDay, seatCountText, seatLabel } from "../../../lib/seats";

const searchSchema = z.object({
  /** Opens a sheet on arrival (the trial-ended strip's Choose plan). */
  sheet: z.enum(["change", "choose"]).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/billing/")({
  staticData: { crumb: { label: "Billing", parents: [] } },
  beforeLoad: requireAdmin,
  validateSearch: (s) => searchSchema.parse(s),
  loader: ({ context }) => context.queryClient.prefetchQuery(planQuery),
  component: PlanTab,
});

function ScheduledRow({ c }: { c: ScheduledSeatChange }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => plans.cancelChange(c.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: planKeys.plan });
      toast(`${c.name} keeps ${seatLabel(c.from, c.from_marketing)}.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <BillingRow
      label={c.name}
      sub={`${seatLabel(c.from, c.from_marketing)} to ${seatLabel(c.to, c.to_marketing)}, ${billingDay(c.effective_at)}`}
    >
      <Button size="sm" busy={cancel.isPending} onClick={() => cancel.mutate()}>
        Cancel
      </Button>
    </BillingRow>
  );
}

/** Members' seats moving at the period end; only when there are some. */
function ScheduledChanges() {
  const plan = useQuery(planQuery);
  const list = plan.data?.scheduled ?? [];
  if (!list.length) return null;
  return (
    <BillingCard title="Scheduled changes">
      {list.map((c) => (
        <ScheduledRow key={c.id} c={c} />
      ))}
    </BillingCard>
  );
}

function PlanTab() {
  const summary = useQuery(billingSummaryQuery);
  if (summary.isPending) return <Loading />;
  if (summary.isError) return <ErrorState error={summary.error} onRetry={() => summary.refetch()} />;
  return summary.data === null ? <BillingOff /> : <PlanOn s={summary.data} />;
}

/** The payment method row's value: the logo tile and name, or "None". */
function MethodValue({ s }: { s: BillingSummary }) {
  const pm = s.payment_method;
  if (!pm) return <span className="text-ink-3 text-ui">None</span>;
  const declined = s.status === "past_due" || (s.status === "suspended" && !trialEnded(s));
  return (
    <Link
      to="/admin/billing/payment-methods"
      className="-mr-1 inline-flex items-center gap-2 rounded-md py-0.5 pr-1 text-ui hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--vd-focus)] focus-visible:outline-none"
    >
      <span className="max-sm:hidden">
        <PmLogo pm={pm} />
      </span>
      <span>{pmName(pm)}</span>
      {declined ? <Badge tone="danger">Declined</Badge> : null}
      <ChevronRight aria-hidden className="size-3.5 text-ink-3" />
    </Link>
  );
}

function PlanOn({ s }: { s: BillingSummary }) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [sheet, setSheet] = useState<SheetMode | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const more = useRef<HTMLButtonElement>(null);
  const resume = useResumePlan();
  const mode: SheetMode = choosesPlan(s) ? "choose" : "change";

  // A link with ?sheet= (the trial-ended strip) opens the sheet once, then the address drops it.
  useEffect(() => {
    if (!search.sheet || s.internal) return;
    setSheet(mode);
    void navigate({ search: {}, replace: true });
  }, [search.sheet, s.internal, mode, navigate]);

  return (
    <div className="flex max-w-[880px] flex-col gap-5">
      <PlanCard
        s={s}
        moreRef={more}
        onChange={() => setSheet(mode)}
        onCancel={() => setCancelOpen(true)}
        onResume={() => resume.mutate()}
        resuming={resume.isPending}
      />
      <SeatsCard s={s} />
      <ScheduledChanges />
      <ChangePlanSheet
        summary={s}
        mode={sheet ?? mode}
        open={sheet !== null}
        onClose={() => setSheet(null)}
      />
      <CancelPlanDialog
        summary={s}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        returnFocus={more}
      />
    </div>
  );
}

function PlanCard({
  s,
  moreRef,
  onChange,
  onCancel,
  onResume,
  resuming,
}: {
  s: BillingSummary;
  moreRef: RefObject<HTMLButtonElement | null>;
  onChange: () => void;
  onCancel: () => void;
  onResume: () => void;
  resuming: boolean;
}) {
  if (s.internal || s.status === "internal") {
    return (
      <BillingCard title="Plan">
        <BillingRow label="Billing">
          <span className="text-ui">Internal</span>
        </BillingRow>
      </BillingCard>
    );
  }

  if (choosesPlan(s)) {
    const ended = s.status !== "trialing";
    const ends = s.trial_ends_at ? dayOf(s.trial_ends_at) : null;
    return (
      <BillingCard
        title="Plan"
        actions={
          <Button size="sm" variant="primary" onClick={onChange}>
            Choose plan
          </Button>
        }
      >
        {s.status === "canceled" ? (
          <BillingRow label="Billing">
            <span className="text-ui">Canceled</span>
          </BillingRow>
        ) : (
          <BillingRow label="Trial" sub={ends ? `${ended ? "Ended" : "Ends"} ${ends}` : undefined}>
            <span className="text-ui tnum">{trialLine(s)}</span>
          </BillingRow>
        )}
        <BillingRow label="Payment method">
          <MethodValue s={s} />
        </BillingRow>
      </BillingCard>
    );
  }

  const ending = canceling(s);
  const busy = updating(s);
  const pastDue = s.status === "past_due" || s.status === "suspended";
  const next = s.renews_at ?? s.period_end;
  const actions = ending ? (
    <>
      <Badge tone="warn">Ending</Badge>
      <Button size="sm" variant="primary" busy={resuming} disabled={busy} onClick={onResume}>
        Resume plan
      </Button>
    </>
  ) : (
    <>
      {pastDue ? <Badge tone="danger">Past due</Badge> : null}
      {busy ? (
        <Badge>
          <Spinner size={12} label="" />
          Updating
        </Badge>
      ) : null}
      <Button size="sm" disabled={busy} onClick={onChange}>
        Change plan
      </Button>
      <Menu
        trigger={
          <IconButton ref={moreRef} label="More" icon={<MoreHorizontal />} size="sm" disabled={busy} />
        }
        items={[{ label: "Cancel plan", onSelect: onCancel }]}
      />
    </>
  );
  return (
    <BillingCard title="Plan" actions={actions}>
      <BillingRow label="Billing">
        <span className="text-ui">{s.interval === "year" ? "Annual" : "Monthly"}</span>
      </BillingRow>
      {ending ? (
        <BillingRow label="Ends">
          <span className="text-ui tnum">{s.cancel_at ? dayOf(s.cancel_at) : ""}</span>
        </BillingRow>
      ) : (
        <BillingRow label="Next invoice">
          <span className="text-ui tnum">
            {next ? `${dayOf(next)}, ${fmtCents(renewalCents(s))}` : fmtCents(renewalCents(s))}
          </span>
        </BillingRow>
      )}
      <BillingRow label="Payment method">
        <MethodValue s={s} />
      </BillingRow>
    </BillingCard>
  );
}

function SeatsCard({ s }: { s: BillingSummary }) {
  const internal = s.internal || s.status === "internal";
  return (
    <BillingCard title="Seats">
      {s.items.map((i) => {
        const full = !internal && i.purchased > 0 && i.assigned >= i.purchased;
        const drop = i.next_period !== null && i.next_period < i.purchased;
        return (
          <BillingRow
            key={i.plan}
            label={PLAN_LABEL[i.plan]}
            sub={
              drop && i.next_period !== null
                ? `${i.purchased - i.next_period} ${i.purchased - i.next_period === 1 ? "seat" : "seats"} removed at renewal${s.period_end ? ` on ${dayOf(s.period_end)}` : ""}`
                : undefined
            }
          >
            {full ? <Badge tone="warn">Full</Badge> : null}
            <span className="text-ui tnum">
              {internal ? `${i.assigned} assigned, no cap` : `${i.assigned} of ${i.purchased}`}
            </span>
          </BillingRow>
        );
      })}
    </BillingCard>
  );
}

/** Billing off (`VD_BILLING` unset): today's seats and the "Payment: Not set up" row. */
function BillingOff() {
  const plan = useQuery(planQuery);
  if (plan.isPending) return <Loading />;
  if (plan.isError) return <ErrorState error={plan.error} onRetry={() => plan.refetch()} />;
  const p = plan.data;
  return (
    <div className="flex max-w-[880px] flex-col gap-5">
      <BillingCard title="Seats">
        {ALL_PLANS.map((pl) => {
          const c = p.seats.find((x) => x.plan === pl) ?? { plan: pl, purchased: null, assigned: 0 };
          const full = c.purchased !== null && c.assigned >= c.purchased;
          return (
            <BillingRow key={pl} label={PLAN_LABEL[pl]}>
              {full ? <Badge tone="warn">Full</Badge> : null}
              <span className="text-ui tnum">
                {c.purchased === null ? `${c.assigned} assigned, no cap` : seatCountText(c)}
              </span>
            </BillingRow>
          );
        })}
      </BillingCard>
      <ScheduledChanges />
      <BillingCard title="Billing">
        <BillingRow label="Payment">
          <span className="text-ink-3 text-ui">{p.billing.provider ?? "Not set up"}</span>
        </BillingRow>
      </BillingCard>
    </div>
  );
}
